/**
 * Utility function to map properties from one class instance to another,
 * removing any properties with undefined values from the target instance.
 *
 * @template T - The source type.
 * @template U - The target type (must be an object).
 * @param source - The source object whose properties will be mapped.
 * @param targetClass - The constructor of the target class.
 * @returns An instance of the target class with mapped properties from the source object.
 */
export function mapInstanceProperties<T extends object, U extends object>(source: T, targetClass: new () => U): U {
	const targetInstance = new targetClass();

	// Use the `init` method if it exists, otherwise map properties manually
	if (typeof (targetInstance as any).init === 'function') {
		(targetInstance as any).init(source);
	} else {
		Object.keys(source).forEach((key) => {
			if (key in targetInstance) {
				(targetInstance as any)[key] = (source as any)[key];
			}
		});
	}

	// Remove undefined properties from the target instance
	Object.keys(targetInstance).forEach((key) => {
		if ((targetInstance as any)[key] === undefined) {
			delete (targetInstance as any)[key];
		}
	});

	return targetInstance;
}
